import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Lock, Unlock, ShieldCheck, Shield, Activity, FolderInput, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import BackupRestorePanel from '@/components/BackupRestorePanel';
import ScheduledBackupSettings from '@/components/ScheduledBackupSettings';
import HealthDashboard from '@/components/HealthDashboard';
import MarkdownImportDialog from '@/components/MarkdownImportDialog';
import NotificationSettings from '@/components/NotificationSettings';
import PushNotificationSettings from '@/components/PushNotificationSettings';

const TABS = [
  { id: 'security', label: 'Security', icon: ShieldCheck },
  { id: 'backup', label: 'Backup', icon: Shield },
  { id: 'health', label: 'Health', icon: Activity },
  { id: 'notifications', label: 'Alerts', icon: Clock },
];

export default function SettingsDialog({ onClose }) {
  const { isEncryptionEnabled, enableEncryption, disableEncryption, isVaultLocked, unlockVault, encryptionPassword, user } = useAuth();
  const { decryptAllNotes } = useApp();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('security');
  const [showMarkdownImport, setShowMarkdownImport] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showEnableConfirm, setShowEnableConfirm] = useState(false);
  const [enablePassword, setEnablePassword] = useState('');

  const hasPasswordInMemory = !!encryptionPassword;

  const handleToggleEncryption = async () => {
    if (isEncryptionEnabled) {
      setShowDisableConfirm(true);
      return;
    }
    if (hasPasswordInMemory) {
      setLoading(true);
      try {
        await enableEncryption(encryptionPassword);
        toast.success('Encryption enabled. Notes are being encrypted.');
      } catch {
        toast.error('Failed to enable encryption');
      }
      setLoading(false);
    } else {
      setShowEnableConfirm(true);
    }
  };

  const handleConfirmEnable = async () => {
    if (!enablePassword) {
      toast.error('Enter your account password');
      return;
    }
    setLoading(true);
    try {
      await authAPI.verifyPassword(enablePassword);
    } catch {
      toast.error('Incorrect account password');
      setLoading(false);
      return;
    }
    try {
      await enableEncryption(enablePassword);
      setShowEnableConfirm(false);
      setEnablePassword('');
      toast.success('Encryption enabled. Notes are being encrypted.');
    } catch {
      toast.error('Failed to enable encryption');
    }
    setLoading(false);
  };

  const handleConfirmDisable = async () => {
    const pwd = disablePassword || encryptionPassword;
    if (!pwd) {
      toast.error('Enter your account password to decrypt notes');
      return;
    }
    setLoading(true);
    try {
      const result = await decryptAllNotes(pwd);
      if (result.failed > 0) {
        toast.error(`${result.failed} notes could not be decrypted. Aborting — your data is safe.`);
        setLoading(false);
        return;
      }
      await disableEncryption();
      setShowDisableConfirm(false);
      setDisablePassword('');
      toast.success(`Encryption disabled. ${result.decrypted} notes decrypted.`);
    } catch (err) {
      toast.error('Failed to disable encryption: ' + (err.message || 'Unknown error'));
    }
    setLoading(false);
  };

  const handleUnlock = async () => {
    if (!password) return;
    setLoading(true);
    const ok = await unlockVault(password);
    if (ok) {
      toast.success('Vault unlocked');
      setPassword('');
    } else {
      toast.error('Invalid password');
    }
    setLoading(false);
  };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] p-0" data-testid="settings-dialog">
          <DialogHeader className="px-5 pt-5 pb-0">
            <DialogTitle>Settings</DialogTitle>
            <DialogDescription>Manage privacy, backups, and system health.</DialogDescription>
          </DialogHeader>

          {/* Tab Bar */}
          <div className="flex border-b border-border/50 px-5 gap-1" data-testid="settings-tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-accent-amber text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`settings-tab-${tab.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <ScrollArea className="max-h-[65vh] px-5 pb-5">
            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6 py-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-accent-amber" />
                    <h3 className="text-sm font-semibold">Client-Side Encryption</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    When enabled, note content is encrypted using AES-256-GCM before leaving your device.
                    The server only stores encrypted data.
                  </p>

                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border">
                    <div className="flex items-center gap-2">
                      {isEncryptionEnabled ? <Lock className="w-4 h-4 text-accent-amber" /> : <Unlock className="w-4 h-4 text-muted-foreground" />}
                      <Label className="text-sm">Encryption</Label>
                    </div>
                    <Switch
                      checked={isEncryptionEnabled}
                      onCheckedChange={handleToggleEncryption}
                      disabled={loading}
                      data-testid="settings-encryption-toggle"
                    />
                  </div>

                  {showEnableConfirm && (
                    <div className="space-y-2 p-3 bg-accent-amber/10 rounded-sm border border-accent-amber/30" data-testid="settings-enable-confirm">
                      <p className="text-xs text-accent-amber font-medium">Confirm: Enable Encryption</p>
                      <p className="text-xs text-muted-foreground">Enter your account password to derive the encryption key. All existing notes will be encrypted.</p>
                      <Input type="password" value={enablePassword} onChange={(e) => setEnablePassword(e.target.value)}
                        placeholder="Account password" className="h-8 text-sm" data-testid="settings-enable-password" />
                      <div className="flex gap-2">
                        <Button onClick={handleConfirmEnable} size="sm" className="flex-1" disabled={loading} data-testid="settings-confirm-enable-btn">
                          {loading ? 'Encrypting...' : 'Confirm & Enable'}
                        </Button>
                        <Button onClick={() => { setShowEnableConfirm(false); setEnablePassword(''); }} size="sm" variant="outline" disabled={loading}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {showDisableConfirm && (
                    <div className="space-y-2 p-3 bg-red-500/10 rounded-sm border border-red-500/30" data-testid="settings-disable-confirm">
                      <p className="text-xs text-red-400 font-medium">Confirm: Disable Encryption</p>
                      <p className="text-xs text-muted-foreground">All encrypted notes will be decrypted and stored as plaintext. Enter your account password to proceed.</p>
                      <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
                        placeholder="Account password" className="h-8 text-sm" data-testid="settings-disable-password" />
                      <div className="flex gap-2">
                        <Button onClick={handleConfirmDisable} size="sm" variant="destructive" className="flex-1" disabled={loading} data-testid="settings-confirm-disable-btn">
                          {loading ? 'Decrypting...' : 'Decrypt & Disable'}
                        </Button>
                        <Button onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); }} size="sm" variant="outline" disabled={loading}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {isVaultLocked && (
                    <div className="space-y-2 p-3 bg-accent-amber/10 rounded-sm border border-accent-amber/30">
                      <p className="text-xs text-accent-amber">Vault is locked. Enter your account password to decrypt notes.</p>
                      <div className="flex gap-2">
                        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                          placeholder="Account password" className="flex-1 h-8 text-sm" data-testid="settings-unlock-password" />
                        <Button onClick={handleUnlock} size="sm" disabled={loading} data-testid="settings-unlock-btn">Unlock</Button>
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground/70 space-y-1 p-3 bg-secondary/20 rounded-sm">
                    <p className="font-medium text-muted-foreground">What's encrypted:</p>
                    <p>Note body content (AES-256-GCM)</p>
                    <p className="font-medium text-muted-foreground mt-2">What's visible to the server:</p>
                    <p>Note titles, tags, notebooks, timestamps, metadata</p>
                    <p className="font-medium text-muted-foreground mt-2">Key management:</p>
                    <p>Key derived from password via PBKDF2 (100k iterations). Stored in session only.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Tab */}
            {activeTab === 'backup' && (
              <div className="space-y-6 py-2">
                <BackupRestorePanel />

                <Separator />

                <ScheduledBackupSettings />

                <Separator />

                {/* Markdown Import */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FolderInput className="w-4 h-4 text-accent-cyan" />
                    <h3 className="text-sm font-semibold">Import</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Import markdown files or folders. Folders are mapped to notebooks. Frontmatter is parsed for titles.
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setShowMarkdownImport(true)} data-testid="open-markdown-import-btn">
                    <FolderInput className="w-3.5 h-3.5 mr-1.5" /> Import Markdown
                  </Button>
                </div>
              </div>
            )}

            {/* Health Tab */}
            {activeTab === 'health' && (
              <div className="py-2">
                <HealthDashboard />
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6 py-2">
                <NotificationSettings />
                <Separator />
                <PushNotificationSettings />
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {showMarkdownImport && (
        <MarkdownImportDialog open={showMarkdownImport} onClose={() => setShowMarkdownImport(false)} />
      )}
    </>
  );
}
